/**
 * useRequestNotificationPermission - Hook to manually request notification permission
 * Replaces notification.requestPermission() from the old store
 */
import { useState, useCallback } from 'react';
import { getToken } from 'firebase/messaging';
import { useBackend } from './useBackend';
import { FirebaseMessaging } from '../Store/Firebase/firebase';
import { Config } from '../Config/Config';
import { logger } from '../Utils/logger';

/**
 * Hook to manually request notification permission (e.g., when scheduling events)
 * Use this in components that need to request permission on user action
 */
export function useRequestNotificationPermission() {
  const backend = useBackend();
  const [isRequesting, setIsRequesting] = useState(false);

  const requestPermission = useCallback(async (): Promise<void> => {
    if (isRequesting) return;

    setIsRequesting(true);
    try {
      const result = await Notification.requestPermission();

      if (result === 'granted' && FirebaseMessaging) {
        // Get FCM token and register it
        const fcmToken = await getToken(FirebaseMessaging, {
          vapidKey: Config.firebase.messaging.publicKey,
        });

        if (fcmToken) {
          logger('useRequestNotificationPermission::registerToken', fcmToken);
          await backend.post('/messaging/notification', {
            token: fcmToken,
            app: 'coach-web',
            type: 'web',
          });
        }
      }
    } catch (err) {
      logger('useRequestNotificationPermission::error', err);
      throw err;
    } finally {
      setIsRequesting(false);
    }
  }, [backend, isRequesting]);

  return {
    requestPermission,
    isRequesting,
  };
}
